import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { User } from '../user';
import { FileObject } from 'react-mui-dropzone';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import PhotoService, { UploadPhotosDictionary } from './PhotoService';
import { RemoteData, isSuccess } from '../utils/remote-data';
import HairLossUpload from './HairLossUpload';
import LicensePhotoUpload from './LicensePhotoUpload';
import ReviewHairLossPhotos from './ReviewHairLossPhotos';
import ReviewLicensePhoto from './ReviewLicensePhoto';
import {
  uploadHairLossPhotosHash,
  uploadLicensePhotoHash,
  reviewHairLossPhotosHash,
  reviewLicensePhotoHash,
  photosSuccessfullyUploadedHash,
  allPossiblePhotoUploadHashes,
  trackYourProgressHash,
} from './constants';
import { ErrorToast, SuccessToast } from '../common/ui-component';
import { OrderFileType, RemoteFile } from '../files';
import * as amplitude from '@amplitude/analytics-browser';
import OrderService from '../orders/OrderService';
import PhotosAreLoading from './PhotosAreLoading';
import PhotosSuccessfullyUploadedPage from './PhotosSuccessfullyUploadedPage';
import TrackYourProgress from './TrackYourProgress';
import EventService from '../events/analytics-events.service';
import { CustomProperties } from '../events/analytics-event.dto';
import { FeatureToggleHelper } from '../utils/feature-toggle';

export async function dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
  const res: Response = await fetch(dataUrl);
  const blob: Blob = await res.blob();
  return new File([blob], fileName, { type: 'image/jpeg' });
}

type Props = {
  user: User;
  existingPhotos: RemoteFile[];
  refetchPhotos: (userId: number) => Promise<RemoteData<RemoteFile[], unknown>>;
};

const PhotosPage = (props: Props) => {
  const { hash, pathname, search } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('photosPage');
  const [otherPhotos, setOtherPhotos] = useState<FileObject[]>([]);
  const [licensePhoto, setLicensePhoto] = useState<FileObject | undefined>(undefined);
  const [showUploadAllRequiredPhotosErrorToast, toggleShowUploadAllRequiredPhotosErrorToast] = useState(false);
  const [showSomethingWentWrongErrorToast, toggleShowSomethingWentWrongErrorToast] = useState(false);
  const [showPhotosNotUploadedErrorToast, toggleShowPhotosNotUploadedErrorToast] = useState(false);
  const [showSuccessToast, toggleShowSuccessToast] = useState(false);
  const [cameraDetectedAndAllowed, setCameraDetectedAndAllowed] = useState(true);
  const [uploadPhotos] = PhotoService.useUploadPhotos();
  const [getCurrentUserLastOrder] = OrderService.useGetUserLastOrder();
  const [processedWithoutPhotosMode, setProcessedWithoutPhotosMode] = useState(false);
  const [photosAreUploading, setPhotosAreUploading] = useState(false);
  const [sendAnalyticsEvent] = EventService.useSendAnalyticsEvent();

  const hasOther = props.existingPhotos.some((x) => x.type === 'other');
  const hasLicense = props.existingPhotos.some((x) => x.type === 'license');

  const getCookie = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return (parts.pop() || '').split(';').shift();
  };

  const [deviceId, setDeviceId] = useState<string | undefined>(getCookie('hh_session_id'));

  useEffect(() => {
    (async () => {
      if (!deviceId) {
        const deviceIdToSet = crypto.randomUUID();
        setDeviceId(deviceIdToSet);
        amplitude.setDeviceId(deviceIdToSet);
      }
      const lastOrder = await getCurrentUserLastOrder(props.user.id);
      if (isSuccess(lastOrder)) {
        if (!!lastOrder.value && lastOrder.value.status !== 'pending-photos' && !hasOther && !hasLicense) {
          setProcessedWithoutPhotosMode(true);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [hash]);

  useEffect(() => {
    if (hasOther || hasLicense) {
      if (
        hash !== uploadHairLossPhotosHash &&
        hash !== reviewHairLossPhotosHash &&
        hash !== photosSuccessfullyUploadedHash
      ) {
        navigate(`${pathname}${search}${trackYourProgressHash}`);
      }
    } else if (!allPossiblePhotoUploadHashes.includes(hash)) {
      navigate(`${pathname}${search}${uploadHairLossPhotosHash}`);
    }
  }, [hasLicense, hasOther, hash, navigate, pathname, props.existingPhotos, search]);

  const adjustNavbarVisibility = useCallback((shouldHideNavbar: boolean) => {
    const navBar = document.getElementsByClassName('woo-navbar').item(0);
    const wrapper = document.getElementsByClassName('tailwindcss-wrapper').item(0);
    if (!shouldHideNavbar) {
      navBar?.classList.add('visibility-hidden');
      if (wrapper?.classList.contains('tw-min-h-[calc(100vh-184px)]')) {
        wrapper?.classList.remove('tw-min-h-[calc(100vh-184px)]');
      }
      wrapper?.classList.add('tw-min-h-[calc(100vh-100px)]');
    } else {
      if (navBar?.classList.contains('visibility-hidden')) {
        navBar?.classList.remove('visibility-hidden');
      }
      if (wrapper?.classList.contains('tw-min-h-[calc(100vh-100px)]')) {
        wrapper?.classList.remove('tw-min-h-[calc(100vh-100px)]');
      }
      wrapper?.classList.add('tw-min-h-[calc(100vh-184px)]');
    }
  }, []);

  const getUploadPhotosPayload = (): UploadPhotosDictionary => {
    const payload: UploadPhotosDictionary = {};
    if (licensePhoto) {
      payload[v4()] = {
        finalPhoto: licensePhoto?.file,
        photoType: 'license',
      };
    }

    otherPhotos.forEach((fileObject) => {
      payload[v4()] = {
        finalPhoto: fileObject.file,
        photoType: 'other',
      };
    });
    return payload;
  };

  const handleClickUploadPhotos = async (user: User) => {
    setPhotosAreUploading(true);
    const payload: UploadPhotosDictionary = getUploadPhotosPayload();
    await uploadPhotos({ data: payload, userId: user.id }).then(async (response) => {
      if (!isSuccess(response) || !response.value) {
        toggleShowSomethingWentWrongErrorToast(true);
      } else if (response.value === 'error1') {
        toggleShowPhotosNotUploadedErrorToast(true);
      } else if (response.value === 'done283') {
        toggleShowSuccessToast(true);
        setOtherPhotos([]);
        setLicensePhoto(undefined);

        if (
          FeatureToggleHelper.isEnable(process.env.REACT_APP_REDIRECT_TO_NEW_LIE_AFTER_ONBOARDING_PHOTOS_FLOW_TOGGLER)
        ) {
          window.location.href =
            `${process.env.REACT_APP_LOGGED_IN_EXPERIENCE_URL}` +
            `${process.env.REACT_APP_LOGGED_IN_EXPERIENCE_SILENT_AUTH_PATH}` +
            `?callbackUrl=${process.env.REACT_APP_LOGGED_IN_EXPERIENCE_URL}finish-activation`;
        } else {
          if (hasOther || hasLicense) {
            await props.refetchPhotos(user.id);
            navigate(`${pathname}${search}${trackYourProgressHash}`);
          } else {
            await props.refetchPhotos(user.id);
            navigate(`${pathname}${search}${photosSuccessfullyUploadedHash}`);
          }
        }
      }
      setPhotosAreUploading(false);
    });
  };

  const handleTakePictureClick = async (image: string, type: OrderFileType, onClose: () => void) => {
    const file = await dataUrlToFile(image, `${v4()}.jpeg`);
    const fileObject = { file, data: image };
    if (type === 'license') {
      setLicensePhoto(fileObject);
      onClose();
      return;
    }
    setOtherPhotos([...otherPhotos, fileObject]);
    onClose();
  };

  const handleUploadPhotos = (newFiles: FileObject[], type: string) => {
    if (type === 'license') {
      setLicensePhoto(newFiles[0]);
      return;
    }
    setOtherPhotos([...otherPhotos, ...newFiles.map((fileObject) => fileObject)]);
  };

  const handleDeletePhoto = (deletedFileObject: FileObject, index: number, type: string) => {
    if (type === 'license') {
      setLicensePhoto(undefined);
      return;
    }
    const newTriedFiles = [...otherPhotos];
    newTriedFiles.splice(index, 1);
    setOtherPhotos(newTriedFiles);

    handleSendAnalyticsEvent('Clicked CTA Delete photo', { CTA_name: 'Delete photo' }, {});

    if (!newTriedFiles.length) {
      navigate(`${pathname}${search}${uploadHairLossPhotosHash}`);
    }
  };

  const handleHairLossPhotosDoneClick = async (user: User) => {
    amplitude.track('photo_flow_hair_success');
    handleSendAnalyticsEvent('Uploaded Photo', { CTA_name: 'Photo Submitted', photo_volume: otherPhotos.length }, {});
    if (user.meta.is_id_required && !user.meta.is_video_required && !hasLicense) {
      navigate(`${pathname}${search}${uploadLicensePhotoHash}`);
    } else {
      await handleClickUploadPhotos(user);
    }
  };

  const handleReviewLicenseDoneClick = async (user: User) => {
    amplitude.track('photo_flow_id_success');
    handleSendAnalyticsEvent('Uploaded ID', { CTA_name: 'ID submitted' }, {});
    const needsLicense = user.meta.is_id_required && !user.meta.is_video_required && !licensePhoto;
    if (needsLicense || otherPhotos.length === 0) {
      toggleShowUploadAllRequiredPhotosErrorToast(true);
      return;
    }
    await handleClickUploadPhotos(user);
  };

  adjustNavbarVisibility(hasOther);

  const handleSendAnalyticsEvent = (
    eventName: string,
    eventProperties: CustomProperties,
    userProperties: CustomProperties,
  ) => {
    const baseEventProperties = {
      eventSource: 'myhappyhead',
      Referrer: (typeof document !== 'undefined' && document.referrer) || '',
      referringDomain: typeof document !== 'undefined' && document.referrer ? new URL(document.referrer).hostname : '',
      'Referring Domain':
        typeof document !== 'undefined' && document.referrer ? new URL(document.referrer).hostname : '',
      'Page Domain': (typeof window.location !== 'undefined' && window.location.hostname) || '',
      'Page Location': (typeof window.location !== 'undefined' && window.location.href) || '',
      'Page Path': (typeof window.location !== 'undefined' && window.location.pathname) || '',
      'Page Title': (typeof document !== 'undefined' && document.title) || '',
      'Page URL': (typeof window.location !== 'undefined' && window.location.href.split('?')[0]) || '',
    };
    sendAnalyticsEvent({
      eventName,
      customEventProperties: { ...baseEventProperties, ...eventProperties },
      customUserProperties: userProperties,
      userId: props.user.id,
      deviceId,
    });
  };

  if (!!photosAreUploading)
    return (
      <div className={`lg:tw-pb-28 tw-max-w-full ${!hasOther ? 'tw-pt-12' : ''}`}>
        <PhotosAreLoading />
      </div>
    );

  const disableTakePictureButton = !cameraDetectedAndAllowed;
  return (
    <>
      <div className={`lg:tw-pb-28 tw-max-w-full ${!hasOther ? 'tw-pt-6' : ''}`}>
        {hash.startsWith(uploadHairLossPhotosHash) && (
          <HairLossUpload
            handleDeletePhoto={handleDeletePhoto}
            handleUploadPhotos={handleUploadPhotos}
            otherPhotos={otherPhotos}
            handleTakePictureClick={handleTakePictureClick}
            disableTakePictureButton={disableTakePictureButton}
            setCameraDetectedAndAllowed={setCameraDetectedAndAllowed}
            processedWithoutPhotosMode={processedWithoutPhotosMode}
            existingPhotos={props.existingPhotos}
            onContinueWithoutUploadingClick={() => handleHairLossPhotosDoneClick(props.user)}
            driverLicenseRequired={props.user.meta.is_id_required}
            videoRequired={props.user.meta.is_video_required}
            sendAnalyticsEvent={handleSendAnalyticsEvent}
          />
        )}
        {hash.startsWith(uploadLicensePhotoHash) && (
          <LicensePhotoUpload
            handleDeletePhoto={handleDeletePhoto}
            handleUploadPhotos={handleUploadPhotos}
            otherPhotos={otherPhotos}
            handleTakePictureClick={handleTakePictureClick}
            disableTakePictureButton={disableTakePictureButton}
            setCameraDetectedAndAllowed={setCameraDetectedAndAllowed}
            sendAnalyticsEvent={handleSendAnalyticsEvent}
          />
        )}
        {hash === reviewHairLossPhotosHash && (
          <ReviewHairLossPhotos
            otherPhotos={otherPhotos}
            handleDeletePhoto={handleDeletePhoto}
            onDoneClick={() => handleHairLossPhotosDoneClick(props.user)}
          />
        )}
        {hash === reviewLicensePhotoHash && (
          <ReviewLicensePhoto
            hasLicense={hasLicense}
            handleDeleteLicensePhoto={() => {
              handleSendAnalyticsEvent('Clicked CTA Delete ID photo', { CTA_name: 'Delete ID photo' }, {});
              setLicensePhoto(undefined);
            }}
            licensePhoto={licensePhoto}
            handleConfirmClick={() => handleReviewLicenseDoneClick(props.user)}
          />
        )}

        {hash === photosSuccessfullyUploadedHash && (
          <PhotosSuccessfullyUploadedPage
            processedWithoutPhotosMode={processedWithoutPhotosMode}
            user={props.user}
            inMemoryPhotos={otherPhotos}
          />
        )}

        {hash === trackYourProgressHash && (
          <TrackYourProgress existingPhotos={props.existingPhotos} sendAnalyticsEvent={handleSendAnalyticsEvent} />
        )}

        {showUploadAllRequiredPhotosErrorToast ? (
          <ErrorToast
            errorMessage={t('pleaseUploadAllRequiredPhotos')}
            onClick={() => toggleShowUploadAllRequiredPhotosErrorToast(false)}
          />
        ) : (
          <></>
        )}
        {showPhotosNotUploadedErrorToast ? (
          <ErrorToast
            errorMessage={t('photosNotUploaded')}
            onClick={() => toggleShowPhotosNotUploadedErrorToast(false)}
          />
        ) : (
          <></>
        )}
        {showSomethingWentWrongErrorToast ? (
          <ErrorToast
            errorMessage={t('somethingWentWrong')}
            onClick={() => toggleShowSomethingWentWrongErrorToast(false)}
          />
        ) : (
          <></>
        )}
        {showSuccessToast ? (
          <SuccessToast text={t('allPhotosUploadedSuccessfully')} onClick={() => toggleShowSuccessToast(false)} />
        ) : (
          <></>
        )}
      </div>
    </>
  );
};

export default PhotosPage;
